import type { QueryClient } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";

export interface OnlineUsersResponse {
  count: number;
}

export const onlineUsersQueryKey = ["online-users"] as const;

type OnlineUsersFetcher = <T>(
  path: string,
  options?: { signal?: AbortSignal },
) => Promise<T>;

interface OnlineUsersQueryOptions {
  queryKey: typeof onlineUsersQueryKey;
  queryFn: (context: { signal: AbortSignal }) => Promise<OnlineUsersResponse>;
  staleTime: number;
  retry: false;
}

export function onlineUsersQueryOptions(
  fetcher: OnlineUsersFetcher = apiGet,
): OnlineUsersQueryOptions {
  return {
    queryKey: onlineUsersQueryKey,
    queryFn: ({
      signal,
    }: {
      signal: AbortSignal;
    }): Promise<OnlineUsersResponse> =>
      fetcher<OnlineUsersResponse>("/online-users", { signal }),
    staleTime: Infinity,
    retry: false,
  } as const;
}

/**
 * Page refreshes own the online-user refresh cadence. A failure here must not
 * turn an otherwise successful catalog or thread refresh into an error.
 */
export async function refreshOnlineUsers(
  queryClient: QueryClient,
  fetcher: OnlineUsersFetcher = apiGet,
): Promise<void> {
  try {
    await queryClient.fetchQuery({
      ...onlineUsersQueryOptions(fetcher),
      // Every page refresh is an explicit refresh, even when the last count is
      // younger than an observer's staleTime.
      staleTime: 0,
    });
  } catch {
    // Online presence is supplementary UI.
  }
}
